/**
 * Gate I8 Phase 4: Workflow Animal Tile Component
 * Gate I8 Phase 5: Added drawer integration
 * 
 * Reusable, staff-friendly tile for displaying an animal in the workflow bucket detail.
 * Shows name, species, location, kennel status, guidance title, and collapsible checklist.
 * Clicking the tile opens the shared Animal Detail Drawer.
 */

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Circle, MapPin, AlertTriangle, ChevronDown, ChevronUp, Calendar, Award, Eye, Plus } from "lucide-react";
import type { WorkflowDashboardItem } from "@/lib/api/workflow-types";
import { TRACK_LABELS } from "@/lib/api/workflow-types";
import { format } from "date-fns";
import { getKennelStatusBadgeClass } from "@/lib/badge-styles";
import { AnimalDetailDrawer } from "@/components/animals/AnimalDetailDrawer";
import { AnimalChecklistProgress } from "@/components/animals/AnimalChecklistProgress";
import { useZoneLabels } from "@/hooks/use-zone-labels";

interface WorkflowAnimalTileProps {
  item: WorkflowDashboardItem;
  isHighlighted?: boolean;
}

const CHECKLIST_VISIBLE_THRESHOLD = 3;

export function WorkflowAnimalTile({ item, isHighlighted = false }: WorkflowAnimalTileProps) {
  const [isChecklistExpanded, setIsChecklistExpanded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { resolveLabel } = useZoneLabels();
  
  const checklist = item.guidance?.guidanceChecklist || [];
  const hasMoreItems = checklist.length > CHECKLIST_VISIBLE_THRESHOLD;
  const visibleChecklist = isChecklistExpanded 
    ? checklist 
    : checklist.slice(0, CHECKLIST_VISIBLE_THRESHOLD);
  
  return (
    <>
    <Card 
      className={`p-4 cursor-pointer hover-elevate transition-all ${isHighlighted ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''}`}
      data-testid="workflow-animal-tile"
      data-animal-id={item.animalId}
      data-highlighted={isHighlighted ? "true" : undefined}
      onClick={() => setIsDrawerOpen(true)}
    >
      <div 
        data-testid={`workflow-animal-tile-${item.animalId}`}
        className="space-y-3"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h4 className="font-semibold text-lg">
              {item.name || item.animalId}
            </h4>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="capitalize">{item.species}</span>
              <span>•</span>
              <MapPin className="h-3 w-3" />
              <span>{item.location}</span>
              {item.zoneCode && <span>• {resolveLabel(item.zoneCode)}</span>}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
              <span 
                className="flex items-center gap-1"
                data-testid={`workflow-animal-tile-adoption-level-${item.animalId}`}
              >
                <Award className="h-3 w-3" />
                Adoption level: {item.adoptionLevel || "Not set"}
              </span>
              <span 
                className="flex items-center gap-1"
                data-testid={`workflow-animal-tile-intake-date-${item.animalId}`}
              >
                <Calendar className="h-3 w-3" />
                Intake date: {item.intakeDate 
                  ? format(new Date(item.intakeDate), "MMM d, yyyy") 
                  : "Not set"}
              </span>
            </div>
            {!item.evalActivated && (
              <a
                href={`/animals/${item.animalId}?tab=behavior`}
                className="flex items-center gap-1 text-sm text-primary w-fit mt-1"
                onClick={(e) => e.stopPropagation()}
                data-testid={`workflow-animal-tile-create-checklist-${item.animalId}`}
              >
                <Plus className="h-3 w-3" />
                Create Checklist
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {item.hasAttentionFlag && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                Needs Attention
              </Badge>
            )}
            {item.isOnHold && (
              <Badge variant="secondary">On Hold</Badge>
            )}
            <Badge className={getKennelStatusBadgeClass(item.guidance?.meta?.kennelStatus?.code)}>{TRACK_LABELS[item.track]}</Badge>
          </div>
        </div>
        
        {item.guidance && (
          <div className="space-y-2">
            <h5 className="font-medium text-sm">{item.guidance.guidanceTitle}</h5>
            
            {checklist.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No checklist items</p>
            ) : (
              <>
                <ul className="space-y-1 text-sm">
                  {visibleChecklist.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Circle className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
                
                {hasMoreItems && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsChecklistExpanded(!isChecklistExpanded);
                    }}
                    className="text-xs h-7 px-2 text-muted-foreground"
                    data-testid={`workflow-animal-tile-toggle-${item.animalId}`}
                  >
                    {isChecklistExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        Show all ({checklist.length - CHECKLIST_VISIBLE_THRESHOLD} more)
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
            
          </div>
        )}

        {item.evalActivated && (
          <AnimalChecklistProgress
            checklistType="evaluation"
            done={item.evalDone}
            total={item.evalTotal}
            className="pt-1"
          />
        )}
      </div>
    </Card>
    
    <AnimalDetailDrawer
      open={isDrawerOpen}
      onOpenChange={setIsDrawerOpen}
      animalId={item.animalId}
      item={item}
      context="checklist"
    />
    </>
  );
}

export default WorkflowAnimalTile;
